import type { DemoOutputQueue, DemoRuleStatus } from "./ruleTypes";

export type RuleOutcomeKey = "pass" | "fail" | "error";

export type RuleRoutingOutcome = {
    id: string;
    when: RuleOutcomeKey;
    status: Exclude<DemoRuleStatus, "skipped">;
    label: string;
    outputQueue?: DemoOutputQueue;
    reasonCode: string;
    explanation: string;
};

export type RuleRoutingConfig = {
    id: string;
    ruleId: string;
    outcomes: RuleRoutingOutcome[];
};

export type RuleRoutingEdit = {
    ruleId: string;
    outcomeId: string;
    patch: Partial<Omit<RuleRoutingOutcome, "id" | "when">>;
};

export class MissingRuleRoutingConfigError extends Error {
    constructor(ruleId: string, when: RuleOutcomeKey) {
        super(`Missing routing outcome '${when}' for rule '${ruleId}'.`);
        this.name = "MissingRuleRoutingConfigError";
    }
}

export const getRuleRoutingOutcome = (
    outcomes: RuleRoutingOutcome[] | undefined,
    when: RuleOutcomeKey,
): RuleRoutingOutcome | undefined => outcomes?.find((outcome) => outcome.when === when);

export const requireRuleRoutingOutcome = (
    outcomes: RuleRoutingOutcome[] | undefined,
    when: RuleOutcomeKey,
    ruleId: string,
): RuleRoutingOutcome => {
    const outcome = getRuleRoutingOutcome(outcomes, when);
    if (!outcome) throw new MissingRuleRoutingConfigError(ruleId, when);
    return outcome;
};

export const applyRuleRoutingEdit = (
    outcomes: RuleRoutingOutcome[] | undefined,
    edit: Omit<RuleRoutingEdit, "ruleId">,
): RuleRoutingOutcome[] | undefined =>
    outcomes?.map((outcome) =>
        outcome.id === edit.outcomeId
            ? {
                  ...outcome,
                  ...edit.patch,
                  id: outcome.id,
                  when: outcome.when,
              }
            : outcome,
    );

export const renderRuleRoutingActionLines = (outcomes: RuleRoutingOutcome[] | undefined): string[] => {
    const failOutcomes = outcomes?.filter((outcome) => outcome.when === "fail" && outcome.label.trim()) ?? [];
    const passOutcomes = outcomes?.filter((outcome) => outcome.when === "pass" && outcome.label.trim()) ?? [];
    const errorOutcomes = outcomes?.filter((outcome) => outcome.when === "error" && outcome.label.trim()) ?? [];
    const preferredOutcomes = failOutcomes.length ? failOutcomes : passOutcomes.length ? passOutcomes : errorOutcomes;

    return Array.from(new Set(preferredOutcomes.map((outcome) => outcome.label.trim()))).map((label) => `ACTION ${label}`);
};
